<template>
  <div class="editable-tag-list">
    <cv-combo-box
      ref="picker"
      :title="title"
      :label="placeholder"
      :disabled="isDisabled"
      :options="unselectedOptions"
      :invalid-message="errorMessage"
      :auto-filter="true"
      @change="onChange"
    >
    </cv-combo-box>
    <cv-tag
      v-for="item in selectedOptions"
      :key="item.value"
      :value="item.value"
      :label="item.label"
      filter
      kind="blue"
      :clear-aria-label="removeLabel"
      @remove="onRemove(item.value)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Option {
  name: string;
  label: string;
  value: string;
}

// Props
interface Props {
  singleSelection?: boolean;
  currentSelection?: unknown[];
  items?: unknown[];
  title?: string;
  placeholder?: string;
  errorMessage?: string;
}

const {
  singleSelection = false,
  currentSelection = [],
  items = [],
  title = "",
  placeholder = "",
  errorMessage = "",
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  remove: [data: unknown];
  change: [data: unknown];
}>();

// Composables
const { t } = useI18n();

// Reactive data
const selectedItems = ref(currentSelection);
const removeLabel = ref(t("editable_tag_remove_label"));

// Computed
const isDisabled = computed(() => {
  if (singleSelection) {
    return selectedItems.value.length === 1;
  }
  return false;
});

const options = computed(() => {
  const options: Option[] = [];
  items.forEach((item) => {
    const nameVal = item.replace(/\W/, "_").toLowerCase();
    options.push({
      name: nameVal,
      label: item,
      value: `val-${nameVal}`,
    });
  });
  return options;
});

const unselectedOptions = computed(() => {
  return options.value.filter(
    (opt) => selectedItems.value && !selectedItems.value.includes(opt.label),
  );
});

const selectedOptions = computed(() => {
  const selectedOptions: Option[] = [];

  if (selectedItems.value) {
    selectedItems.value.forEach((item) => {
      const option = options.value.find((opt) => opt.label === item);
      if (option) {
        const nameVal = item.replace(/\W/, "_").toLowerCase();
        selectedOptions.push({
          name: nameVal,
          label: item,
          value: `val-${nameVal}`,
        });
      }
    });
  }

  return selectedOptions;
});

// Methods
const onRemove = (value: string) => {
  const option = options.value.find((opt) => opt.value === value);
  if (option) {
    const index = selectedItems.value.indexOf(option.label);
    if (index > -1) {
      selectedItems.value.splice(index, 1);
      emit("remove", selectedItems.value);
    }
  }
};

const onChange = (value: string) => {
  if (value !== "") {
    if (selectedItems.value.indexOf(value) === -1) {
      const option = options.value.find((opt) => opt.value === value);
      if (option) {
        selectedItems.value.push(option.label);
      }
    }
    emit("change", selectedItems.value);
  }
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.editable-tag-list {
  label.bx--label {
    font: var(--body-short-01);
    color: var(--text-02);

    margin-bottom: 8px;
  }

  .bx--list-box__wrapper {
    margin-bottom: $spacing-03;
  }

  .bx--list-box {
    .bx--text-input {
      &::placeholder {
        color: $text-03;
      }
    }
  }

  div.cv-tag.bx--tag.bx--tag--filter {
    cursor: initial;
  }

  .bx--tag {
    color: $state-neutral-text;
  }

  .bx--tag__label {
    @include carbon--type-style("label-01", true);
    overflow: visible;
  }
}
</style>
